import { useContext, useState, useEffect } from "react";
import { AppContext } from "../../../../context/appContext";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { AddBranchWrapper } from "./styled";
import Cookies from "universal-cookie";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseButton } from "../../../../components/button/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { addBranchService } from "../../../../utils/api/configuration/branch/addBranch";
import { retrieveUserProfileService } from "../../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveStatesService } from "../../../../utils/outbound/state/states";
import { retrieveStateByIdService } from "../../../../utils/outbound/state/stateById";
import { retrieveLGAsService } from "../../../../utils/outbound/lga/lgas";
import { retrieveAllBranchesService } from "../../../../utils/api/configuration/branch/retrieveAllBranches";
import { useNavigate } from "react-router-dom";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { retrieveBranchIndexService } from "../../../../utils/api/configuration/branch/retrieveBranchIndex";
import { retrieveInstitutionByIdService } from "../../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCountryByIdService } from "../../../../utils/outbound/country/countryById";
import { retrieveOtherCitiesService } from "../../../../utils/outbound/other/cities";
import { retrieveOtherStatesService } from "../../../../utils/outbound/other/states";

export const AddBranch = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        serialNo: "",
        name: "",
        email: "",
        phone: "",
        address: "",
        lga: " ",
        state: " ",
        country: "",
        description: "",
        parent: " ",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    useEffect(() => {
        retrieveBranchIndexService(TOKEN)
            .then((data) => {
                return setFormDetails((prev) => ({ ...prev, serialNo: data.nextIndex }));
            })
            .catch((err) => {
                console.error("Failed to fetch branch index:", err);
            });
    }, [TOKEN]);

    useEffect(() => {
        if (!TOKEN) return;
        const fetchCountry = async () => {
            try {
                const user = await retrieveUserProfileService(TOKEN);
                const institution = await retrieveInstitutionByIdService(user?._id, user?.institution);
                const country = await retrieveCountryByIdService(user?._id, institution.country);
                setFormDetails((prev) => ({ ...prev, country: country?.title }));
            } catch (err) {
                console.error("Failed to fetch country:", err);
            }
        };
        fetchCountry();
    }, [TOKEN]);

    const { data: branches } = useQuery({
			queryKey: ["branchesForAddBranch", TOKEN],
			queryFn: async () => {
				const response = await retrieveAllBranchesService(TOKEN);
				return response.data;
			},
			enabled: !!TOKEN,
		});

    const { data: states } = useQuery({
        queryKey: ['states', TOKEN, formDetails.country],
        queryFn: async () => {
            const user = await retrieveUserProfileService(TOKEN);
            const country = formDetails?.country?.trim() || "";
            if (!country) return;
            let response;
            if (country === "Nigeria") {
                response = await retrieveStatesService(user?._id);
            } else {
                const institution = await retrieveInstitutionByIdService(user?._id, user?.institution);
                const countryInfo = await retrieveCountryByIdService(user?._id, institution.country);
                const res = await retrieveOtherStatesService({ country: countryInfo?.title });
                response = res?.data?.states
            }
            return response;
        },
        enabled: !!TOKEN && !!formDetails.country?.trim(),
    });

    const { data: lgas } = useQuery({
        queryKey: ['LGAs', TOKEN, formDetails.country, formDetails.state],
        queryFn: async () => {
            const user = await retrieveUserProfileService(TOKEN);
            const country = formDetails?.country?.trim() || "";
            if (!country) return;
            const stateId = formDetails.state?.trim() ? formDetails.state : null;
            if (!stateId) return;
            let response;
            if (country === "Nigeria") {
                const state = await retrieveStateByIdService(user?._id, stateId);
                response = await retrieveLGAsService(state.title);
            } else {
                const institution = await retrieveInstitutionByIdService(user?._id, user?.institution);
                const countryInfo = await retrieveCountryByIdService(user?._id, institution.country);
                const res = await retrieveOtherStatesService({ country: countryInfo?.title });
                const state = res?.data?.states?.[Number(stateId) - 1];
                const cities = await retrieveOtherCitiesService({ country: countryInfo?.title, state: state?.name });
                response = cities?.data;
            }
            return response;
        },
        enabled: !!TOKEN && !!formDetails.country?.trim() && !!formDetails.state?.trim(),
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await addBranchService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to create branch. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to create branch. ${error.message}`);
            console.error('Failed to create branch:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Branch"}
            pageTitle={"Add Branch"}
        >
            <AddBranchWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Add Branch Successful"
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Branch List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required Information to add a branch
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Branch ID
                                </BaseLabel>
                                <BaseInput
                                    required
                                    readOnly
                                    name="serialNo"
                                    value={formDetails.serialNo.toString().padStart(3, '0')}
                                    placeholder="Branch ID"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Parent Branch
                                </BaseLabel>
                                <BaseSelect
                                    name="parent"
                                    fontweight={400}
                                    value={formDetails.parent}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Parent Branch
                                    </BaseOption>
                                    {branches?.map((branch: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={branch.id}
                                            >
                                                {branch.name}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Branch Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="name"
                                    value={formDetails.name}
                                    placeholder="Enter Branch Name"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Email Address
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="email"
                                    name="email"
                                    value={formDetails.email}
                                    placeholder="Enter Email"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Phone Number
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="phone"
                                    value={formDetails.phone}
                                    placeholder="Enter Phone Number"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Address
                                </BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="address"
                                    value={formDetails.address}
                                    placeholder="Enter Address"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 4 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Country
                                </BaseLabel>
                                <BaseInput
                                    required
                                    readOnly
                                    name="country"
                                    value={formDetails.country}
                                    placeholder="Country"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 4 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    State/Province
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="state"
                                    fontweight={400}
                                    value={formDetails.state}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select State/Province
                                    </BaseOption>
                                    {states?.map((state: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={String(state.id || index + 1)}
                                            >
                                                {state?.title || state?.name}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 4 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    LGA/City
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="lga"
                                    fontweight={400}
                                    value={formDetails.lga}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select LGA/City
                                    </BaseOption>
                                    {lgas?.map((lga: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={String(lga.id || index + 1)}
                                            >
                                                {lga.title || lga}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Description
                                </BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="description"
                                    value={formDetails.description}
                                    placeholder="Enter Description"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </AddBranchWrapper>
        </AppLayout>
    )
}